import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { ANLAM, DOKUNMA_TABANI_PX } from '../theme';

/**
 * SÖZLÜK SEÇİCİ — SÖZLEŞMENİN ÜÇÜNCÜ ORTAK BİLEŞENİ (F4·B1; madde 142).
 *
 * > **Dar bir açılır liste 260 kalemi taşıyamaz.** Sebep taksonomisi 25
 * > kategori / 260 kaleme çıkınca `Select` bir "aşağı kaydır ve umut et"
 * > kutusuna döndü; bileşen bunun için var.
 *
 * ⚠️ **SÖZLEŞMEDEKİ "MODAL YALNIZ ONAY SORULARI" KISITININ ŞERHİ (P1).**
 * O kısıt **KAYIT OLUŞTURMA** içindir: bir kaydı modalda açmak, kaydın
 * kendi sayfasını ve URL'ini yok eder. **BU BİR SEÇİCİDİR** — kayıt
 * oluşturmaz, bir alanın DEĞERİNİ seçtirir ve kapanır. Farklı iş, farklı
 * desen. *Ayrım kalıcıdır:* modalda **kayıt açılmaz**, modalda **değer
 * seçilir**.
 *
 * ⚠️ **ORTAK BİLEŞEN OLARAK YAZILDI, TEK MÜŞTERİYLE BAĞLANDI.** Aynı sorunu
 * yaşayan iki liste daha var (risk tehlike 33 · risk riski 31 kalem, grup
 * etiketli tek liste — §19/12-m·n) ve yapı onları da alır: `SecilebilirKalem`
 * bir SEBEP kavramı değil, "kategorili sözlük kalemi" kavramıdır. Bu turda
 * BAĞLANMADILAR — mevcut ekranlar toptan dönüştürülmez (sözleşmenin kendi
 * kuralı).
 *
 * ⚠️ **BİLEŞEN KURAL BİLMEZ.** "Diğer" işareti · sağlık sınırı şerhi ·
 * kategori sırası hepsi SUNUCUDAN gelen alanlardır (`digerMi` ·
 * `serh` · `katmanAd`). Ekran kategori kodlarının hangisinin ne anlama
 * geldiğini bilmez ve bilmemelidir (madde 37 dersi).
 */

export interface SecilebilirKalem {
  id: string;
  ad: string;
  /** Üst katman etiketi — iki katmanlı gezinmenin BİRİNCİ basamağı. */
  katmanAd: string;
  /** Kategori başlığı (kod dahil gelebilir) — İKİNCİ basamak. */
  kategoriAd: string;
  /** Kategori sıralaması; yoksa alfabetik. */
  kategoriSira: number | null;
  /** Kategori başlığının altına basılacak sabit şerh (varsa). */
  serh?: string | null;
}

interface Kategori {
  ad: string;
  sira: number;
  serh: string | null;
  kalemler: SecilebilirKalem[];
}

function normalize(metin: string): string {
  return metin
    .toLocaleLowerCase('tr-TR')
    .replaceAll('ı', 'i')
    .replaceAll('İ', 'i')
    .replaceAll('ş', 's')
    .replaceAll('ğ', 'g')
    .replaceAll('ü', 'u')
    .replaceAll('ö', 'o')
    .replaceAll('ç', 'c');
}

export function SozlukSecici({
  kalemler,
  value,
  onChange,
  label,
  placeholder = 'Kalem seçin',
  testId,
  disabled,
}: {
  kalemler: readonly SecilebilirKalem[];
  value: string | null;
  onChange: (id: string | null) => void;
  label: string;
  placeholder?: string;
  testId?: string;
  disabled?: boolean;
}): React.JSX.Element {
  const [acik, setAcik] = useState(false);
  const [arama, setArama] = useState('');
  // SOL KOLON GEZİNMEDİR (F4·B4 — gezinti kapanış turu 2): kategori
  // seçilince sağda YALNIZ o kategorinin kalemleri durur (kaydırmasız);
  // "hepsi birden" görünümü YOKTUR — 260 kalemlik akış tam olarak sol
  // kolonu süse çeviren şeydi. Arama kategori seçimini DEVRE DIŞI bırakır
  // (arama iki katmanı da atlar, doğrudan kaleme gider).
  const [acikKategori, setAcikKategori] = useState<string | null>(null);

  const secili = useMemo(() => kalemler.find((k) => k.id === value) ?? null, [kalemler, value]);

  // İKİ KATMANLI GEZİNME: katman → kategori → kalem. Arama HER İKİ katmanı
  // da atlayarak doğrudan kalemi bulur — 260 kalemde "gezinerek bul" tek
  // başına yetmez, "yazarak bul" tek başına da yetmez (kullanıcı kalemin
  // tam adını bilmez); ikisi YAN YANA durur.
  const katmanlar = useMemo(() => {
    const q = normalize(arama.trim());
    const eslesen = q === '' ? kalemler : kalemler.filter((k) => normalize(k.ad).includes(q));
    const harita = new Map<string, Map<string, Kategori>>();
    for (const kalem of eslesen) {
      const kategoriler = harita.get(kalem.katmanAd) ?? new Map<string, Kategori>();
      const kategori = kategoriler.get(kalem.kategoriAd) ?? {
        ad: kalem.kategoriAd,
        sira: kalem.kategoriSira ?? Number.MAX_SAFE_INTEGER,
        serh: kalem.serh ?? null,
        kalemler: [],
      };
      kategori.kalemler.push(kalem);
      kategoriler.set(kalem.kategoriAd, kategori);
      harita.set(kalem.katmanAd, kategoriler);
    }
    return [...harita.entries()]
      .map(([katmanAd, kategoriler]) => ({
        katmanAd,
        kategoriler: [...kategoriler.values()].sort(
          (a, b) => a.sira - b.sira || a.ad.localeCompare(b.ad, 'tr'),
        ),
      }))
      .sort((a, b) => {
        const aSira = Math.min(...a.kategoriler.map((k) => k.sira));
        const bSira = Math.min(...b.kategoriler.map((k) => k.sira));
        return aSira - bSira;
      });
  }, [kalemler, arama]);

  const bulunan = katmanlar.reduce(
    (toplam, katman) =>
      toplam + katman.kategoriler.reduce((alt, kategori) => alt + kategori.kalemler.length, 0),
    0,
  );

  // VARSAYILAN GÖRÜNÜM: kategori listesi + İLK kategori seçili — kalem
  // listesi boş açılmaz, 260 kalem birden de akmaz.
  const ilkKategoriAd = katmanlar[0]?.kategoriler[0]?.ad ?? null;
  const pencereyiAc = (): void => {
    setAcikKategori((mevcut) => mevcut ?? ilkKategoriAd);
    setAcik(true);
  };

  return (
    <Box>
      <Text component="label" size="sm" fw={500} display="block" mb={4}>
        {label}
      </Text>
      <Button
        variant="default"
        disabled={disabled}
        data-testid={testId}
        onClick={pencereyiAc}
        styles={{
          root: { minHeight: DOKUNMA_TABANI_PX, justifyContent: 'flex-start', width: '100%' },
          label: { whiteSpace: 'normal', textAlign: 'left' },
        }}
      >
        {secili ? secili.ad : placeholder}
      </Button>
      {secili && (
        <Text size="xs" c="dimmed" mt={4} data-testid={testId ? `${testId}-kategori` : undefined}>
          {secili.katmanAd} · {secili.kategoriAd}
        </Text>
      )}

      <Modal
        opened={acik}
        onClose={() => setAcik(false)}
        title={label}
        // B4 — seçici BÜYÜR (neredeyse tam ekran): 25 kategori / 260 kalem
        // dar pencereye sığmaz; solda kategori ağacı, sağda kalemler.
        size="90%"
        data-testid={testId ? `${testId}-pencere` : undefined}
      >
        <Stack gap="xs">
          <TextInput
            placeholder="Ara (arama katman ve kategori atlar — doğrudan kalemi bulur)"
            value={arama}
            autoFocus
            data-testid={testId ? `${testId}-arama` : undefined}
            onChange={(event) => setArama(event.currentTarget.value)}
          />
          <Text size="xs" c="dimmed" data-testid={testId ? `${testId}-sayac` : undefined}>
            {bulunan} kalem
          </Text>
          {katmanlar.length === 0 && (
            <Text size="sm" c="dimmed">
              Aramaya uyan kalem yok.
            </Text>
          )}
          <Group align="flex-start" gap="md" wrap="nowrap">
            {/* SOL — kategori ağacı (iki katman: katman → kategori). Arama
                varken süzülmüş sayılar görünür; tıklamak sağ paneli daraltır. */}
            <ScrollArea.Autosize mah="55vh" type="auto" w={280} style={{ flexShrink: 0 }}>
              <Stack gap="sm" pr="xs">
                {katmanlar.map((katman) => (
                  <Box key={katman.katmanAd}>
                    <Text fw={700} size="xs" tt="uppercase" c="dimmed">
                      {katman.katmanAd}
                    </Text>
                    <Divider my={4} />
                    <Stack gap={2}>
                      {katman.kategoriler.map((kategori) => (
                        <UnstyledButton
                          key={kategori.ad}
                          data-testid={testId ? `${testId}-kategori-${kategori.ad}` : undefined}
                          // Tıklama SEÇER, kapatmaz — "seçimsiz = hepsi"
                          // görünümü kaldırıldı (sol kolon gezinmedir).
                          onClick={() => setAcikKategori(kategori.ad)}
                          style={{
                            minHeight: DOKUNMA_TABANI_PX,
                            display: 'flex',
                            alignItems: 'center',
                            paddingInline: 'var(--mantine-spacing-xs)',
                            borderRadius: 8,
                          }}
                        >
                          <Text size="sm" fw={acikKategori === kategori.ad ? 700 : 400}>
                            {kategori.ad}{' '}
                            <Text span size="xs" c="dimmed">
                              ({kategori.kalemler.length})
                            </Text>
                          </Text>
                        </UnstyledButton>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            {/* SAĞ — YALNIZ seçili kategorinin kalemleri (kaydırmasız).
                ⚠️ Arama varken kategori süzgeci DEVRE DIŞI — arama iki
                katmanı da atlar; seçili kategori eşleşmeyi gizleyemez. */}
            <ScrollArea.Autosize mah="55vh" type="auto" style={{ flex: 1 }}>
              <Stack gap="sm" pr="sm">
                {katmanlar
                  .flatMap((katman) => katman.kategoriler)
                  .filter((kategori) => arama.trim() !== '' || kategori.ad === acikKategori)
                  .map((kategori) => (
                    <Box key={kategori.ad}>
                      <Text fw={600} size="sm">
                        {kategori.ad}
                      </Text>
                      {kategori.serh && (
                        <Alert
                          color={ANLAM.dikkat}
                          mt={4}
                          p="xs"
                          data-testid={testId ? `${testId}-serh` : undefined}
                        >
                          <Text size="xs">{kategori.serh}</Text>
                        </Alert>
                      )}
                      <Stack gap={2} mt={4}>
                        {kategori.kalemler.map((kalem) => (
                          <UnstyledButton
                            key={kalem.id}
                            data-testid={`${testId ?? 'sozluk'}-kalem-${kalem.id}`}
                            onClick={() => {
                              onChange(kalem.id);
                              setAcik(false);
                              setArama('');
                              setAcikKategori(null);
                            }}
                            style={{
                              minHeight: DOKUNMA_TABANI_PX,
                              display: 'flex',
                              alignItems: 'center',
                              paddingInline: 'var(--mantine-spacing-xs)',
                              borderRadius: 8,
                            }}
                          >
                            <Text size="sm">{kalem.ad}</Text>
                          </UnstyledButton>
                        ))}
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            </ScrollArea.Autosize>
          </Group>
          <Group justify="space-between">
            <Button
              variant="subtle"
              disabled={value === null}
              data-testid={testId ? `${testId}-temizle` : undefined}
              onClick={() => {
                onChange(null);
                setAcik(false);
              }}
            >
              Seçimi temizle
            </Button>
            <Button variant="default" onClick={() => setAcik(false)}>
              Kapat
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
